import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileUp, Loader2, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";

export const useIsOwner = () => useQuery({ queryKey: ["me"], queryFn: () => api.get("/auth/me").then((r) => r.data), staleTime: 60_000 }).data?.is_owner === true;

export const downloadXlsx = async (url, filename) => {
  const { data } = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const ExcelButton = ({ url, filename, testId, label = "Download Excel", disabled }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadXlsx(url, filename); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return <button type="button" className="btn light" disabled={busy || disabled} onClick={run} data-testid={testId}>{busy ? <Loader2 size={15} className="spin" /> : <FileDown size={15} />} {label}</button>;
};

export const ExcelTools = ({ title, text, exportUrl, importUrl, filename, onImported, testPrefix }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(importUrl, fd);
      setResult(data);
      toast.success(`${data.updated} updated · ${data.created} added${data.errors.length ? ` · ${data.errors.length} row${data.errors.length === 1 ? "" : "s"} with problems` : ""}`);
      onImported();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="ad-card shop-excel" data-testid={`${testPrefix}-excel-tools`}>
      <div><h3>{title}</h3><p className="muted-sm">{text}</p></div>
      <div className="req-actions">
        <ExcelButton url={exportUrl} filename={filename} testId={`${testPrefix}-excel-download`} disabled={busy} />
        <label className={`btn gold ${busy ? "is-disabled" : ""}`} data-testid={`${testPrefix}-excel-upload-label`}>
          {busy ? <Loader2 size={15} className="spin" /> : <FileUp size={15} />} Upload Excel
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden disabled={busy} onChange={upload} data-testid={`${testPrefix}-excel-upload`} />
        </label>
      </div>
      {result && (
        <div className="shop-excel-result" data-testid={`${testPrefix}-excel-result`}>
          <strong>{result.updated} updated · {result.created} added · {result.skipped} unchanged</strong>
          {result.errors.map((er, i) => <small key={`${i}-${er}`} data-testid={`${testPrefix}-excel-error`}>{er}</small>)}
        </div>
      )}
    </div>
  );
};

export const DataTools = ({ section, label, exportUrl, filename, onPurged, children }) => {
  const [before, setBefore] = useState("");
  const [busy, setBusy] = useState(false);
  const isOwner = useIsOwner();
  const purge = async () => {
    if (!window.confirm(`Permanently delete all ${label} older than ${before}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/${section}/purge`, { before });
      toast.success(`${data.deleted} ${label} deleted`);
      setBefore("");
      onPurged();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="ad-card dt-tools" data-testid={`${section}-data-tools`}>
      <div className="dt-row">
        <ExcelButton url={exportUrl} filename={filename} testId={`${section}-excel-download`} />
        {children}
      </div>
      {isOwner && <div className="dt-row dt-purge">
        <label className="mu-field">Clear {label} older than<input type="date" value={before} onChange={(e) => setBefore(e.target.value)} data-testid={`${section}-purge-date`} /></label>
        <button type="button" className="btn light ad-danger" disabled={!before || busy} onClick={purge} data-testid={`${section}-purge-button`}>{busy ? <Loader2 size={15} className="spin" /> : <Trash2 size={15} />} Delete old {label}</button>
      </div>}
    </div>
  );
};

export const RowDelete = ({ url, name, onDone, testId }) => {
  const remove = async () => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try { await api.delete(url); toast.success("Deleted"); onDone(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return <button type="button" className="btn light ad-danger" onClick={remove} data-testid={testId}><Trash2 size={14} /> Delete</button>;
};
